import { useState } from "react";
import type { RecommendationOutcome } from "./advisorLogic";

interface ResultsViewProps {
  outcome: RecommendationOutcome;
  onBookDemo: () => void;
  onRestart: () => void;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

type Tab = "summary" | "compare";

export default function ResultsView({
  outcome,
  onBookDemo,
  onRestart,
  onClose,
  onNavigate,
}: ResultsViewProps) {
  const [activeTab, setActiveTab] = useState<Tab>("summary");
  const [copied, setCopied] = useState(false);

  const confidencePct = Math.round(outcome.confidence * 100);

  // Bespoke result: no products, direct to sales
  if (outcome.isBespoke) {
    return (
      <div className="animate-fade-in">
        <div className="mb-6 text-center">
          <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-emerald-accent/10 border border-emerald-accent/20 mx-auto mb-3">
            <i className="ri-tools-line text-2xl text-emerald-accent" />
          </div>
          <h4 className="text-base font-bold text-white mb-1">Custom Solution Recommended</h4>
          <p className="text-xs text-silver-400">Your needs call for a bespoke build tailored to your business.</p>
        </div>

        <div className="glass-card p-5 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-lightbulb-line text-emerald-accent text-sm" />
            <h5 className="text-xs font-bold text-white uppercase tracking-wider">Why Bespoke</h5>
          </div>
          <p className="text-sm text-silver-200 leading-relaxed">{outcome.whyThisFits}</p>
        </div>

        <div className="glass-card p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-line-chart-line text-emerald-accent text-sm" />
            <h5 className="text-xs font-bold text-white uppercase tracking-wider">What to Expect</h5>
          </div>
          <ul className="space-y-2.5">
            {outcome.expectedOutcomes.map((o, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-silver-300">
                <i className="ri-check-line text-emerald-accent mt-0.5 text-xs shrink-0" />
                <span>{o}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="glass-card p-4 mb-6 bg-emerald-accent/5 border-emerald-accent/15">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-vip-crown-line text-emerald-accent text-sm" />
            <h5 className="text-xs font-bold text-white uppercase tracking-wider">Engagement Model</h5>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-bold text-emerald-accent">{outcome.planTier}</span>
            <span className="text-xs text-silver-400">Engagement</span>
          </div>
          <p className="text-xs text-silver-300">{outcome.planDescription}</p>
        </div>

        <div className="space-y-2.5">
          <button
            onClick={() => { onNavigate("/contact"); onClose(); }}
            className="btn-primary w-full justify-center"
          >
            <i className="ri-phone-line" />
            Talk to Us
          </button>
          <button
            onClick={onRestart}
            className="w-full text-center text-xs text-silver-500 hover:text-silver-300 transition-colors cursor-pointer py-2"
          >
            Restart Advisor
          </button>
        </div>
      </div>
    );
  }

  const buildEmailBody = () => {
    const lines = [
      "Hi StackMap Team,",
      "",
      "I used the StackMap Product Advisor and received the following recommendation:",
      "",
      `Recommended: ${outcome.primary.map((r) => r.product).join(" + ") || "Bespoke / Custom Solution"}`,
      `Confidence: ${confidencePct}%`,
      `Plan: ${outcome.planTier} \u2014 ${outcome.planDescription}`,
      "",
      "Why it fits:",
      outcome.whyThisFits,
      "",
      "Expected outcomes:",
      ...outcome.expectedOutcomes.map((o) => `- ${o}`),
      "",
      "Please reach out to discuss next steps.",
      "",
      "Regards,",
    ];
    return encodeURIComponent(lines.join("\n"));
  };

  const mailtoLink = `mailto:info@stackmapsystems.com?subject=${encodeURIComponent(
    "StackMap Product Advisor Recommendation"
  )}&body=${buildEmailBody()}`;

  const handleCopy = async () => {
    const text = [
      "StackMap Product Advisor Recommendation",
      "",
      `Recommended: ${outcome.primary.map((r) => r.product).join(" + ") || "Bespoke / Custom Solution"}`,
      `Confidence: ${confidencePct}%`,
      `Plan: ${outcome.planTier} \u2014 ${outcome.planDescription}`,
      "",
      "Why it fits:",
      outcome.whyThisFits,
      "",
      "Expected outcomes:",
      ...outcome.expectedOutcomes.map((o) => `- ${o}`),
    ].join("\n");

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // silently fail
    }
  };

  return (
    <div className="animate-fade-in">
      {/* Hero result */}
      <div className="mb-5 text-center">
        <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-emerald-accent/10 border border-emerald-accent/20 mx-auto mb-3">
          <i className="ri-sparkling-line text-xl text-emerald-accent" />
        </div>
        <h4 className="text-base font-bold text-white mb-1">Recommended for Your Business</h4>
        <p className="text-xs text-silver-400">Based on your answers, here is what fits best.</p>
      </div>

      {/* Confidence + Coverage badges */}
      <div className="flex items-center justify-center gap-3 mb-5">
        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border ${
            confidencePct >= 80
              ? "bg-emerald-accent/10 border-emerald-accent/20 text-emerald-accent"
              : confidencePct >= 60
              ? "bg-yellow-500/10 border-yellow-500/20 text-yellow-400"
              : "bg-silver-400/10 border-silver-400/20 text-silver-300"
          }`}
          title="How closely the recommendation matches your stated needs"
        >
          <span className="w-3 h-3 flex items-center justify-center">
            <i className="ri-bar-chart-box-line" />
          </span>
          Match Confidence: {confidencePct}%
        </div>
        {outcome.needCoverage && (
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-emerald-accent/10 border border-emerald-accent/20 text-emerald-accent"
            title="All stated challenges are covered by the recommended products"
          >
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-shield-check-line" />
            </span>
            Full Need Coverage
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center p-1 bg-graphite-800/60 rounded-lg mb-5 border border-graphite-700/40">
        <button
          onClick={() => setActiveTab("summary")}
          className={`flex-1 text-xs font-semibold py-2 rounded-md transition-colors cursor-pointer whitespace-nowrap ${
            activeTab === "summary"
              ? "bg-emerald-accent/15 text-emerald-accent"
              : "text-silver-400 hover:text-silver-200"
          }`}
        >
          Summary
        </button>
        <button
          onClick={() => setActiveTab("compare")}
          className={`flex-1 text-xs font-semibold py-2 rounded-md transition-colors cursor-pointer whitespace-nowrap ${
            activeTab === "compare"
              ? "bg-emerald-accent/15 text-emerald-accent"
              : "text-silver-400 hover:text-silver-200"
          }`}
        >
          Compare Modules
        </button>
      </div>

      {activeTab === "summary" && (
        <>
          {/* Recommended Solution(s) */}
          <div className="space-y-4 mb-5">
            {outcome.primary.map((rec) => (
              <div key={rec.product} className="glass-card p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-emerald-accent/10 border border-emerald-accent/20 flex-shrink-0">
                    <i className={`${rec.icon} text-emerald-accent text-sm`} />
                  </div>
                  <div>
                    <h5 className="text-sm font-bold text-white">{rec.product}</h5>
                    <p className="text-xs text-silver-300 mt-0.5 leading-relaxed">{rec.tagline}</p>
                  </div>
                </div>
                <p className="text-sm text-silver-200 leading-relaxed mb-3">{rec.description}</p>
                <ul className="space-y-2 pl-1">
                  {rec.bullets.slice(0, 5).map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-silver-300">
                      <span className="w-3.5 h-3.5 flex items-center justify-center rounded-full bg-emerald-accent/10 border border-emerald-accent/20 shrink-0 mt-0.5">
                        <i className="ri-check-line text-emerald-accent text-[8px]" />
                      </span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                {rec.slug && (
                  <button
                    onClick={() => { onNavigate(`/products/${rec.slug}`); onClose(); }}
                    className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-emerald-accent hover:text-white transition-colors cursor-pointer"
                  >
                    Learn more about {rec.product}
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs" />
                    </span>
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Why This Fits */}
          <div className="glass-card p-4 mb-5">
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-lightbulb-line text-emerald-accent text-sm" />
              <h5 className="text-xs font-bold text-white uppercase tracking-wider">Why This Fits</h5>
            </div>
            <p className="text-sm text-silver-200 leading-relaxed">{outcome.whyThisFits}</p>
          </div>

          {/* Expected Outcomes */}
          <div className="glass-card p-4 mb-5">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-line-chart-line text-emerald-accent text-sm" />
              <h5 className="text-xs font-bold text-white uppercase tracking-wider">Expected Outcomes</h5>
            </div>
            <ul className="space-y-2.5">
              {outcome.expectedOutcomes.map((o, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-silver-300">
                  <i className="ri-check-line text-emerald-accent mt-0.5 text-xs shrink-0" />
                  <span>{o}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Suggested Plan Tier */}
          <div className="glass-card p-4 mb-6 bg-emerald-accent/5 border-emerald-accent/15">
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-vip-crown-line text-emerald-accent text-sm" />
              <h5 className="text-xs font-bold text-white uppercase tracking-wider">Suggested Plan</h5>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-bold text-emerald-accent">{outcome.planTier}</span>
              <span className="text-xs text-silver-400">Plan</span>
            </div>
            <p className="text-xs text-silver-300">{outcome.planDescription}</p>
          </div>
        </>
      )}

      {activeTab === "compare" && (
        <div className="mb-6 space-y-3">
          {outcome.primary.map((rec) => (
            <div key={rec.product} className="glass-card p-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-emerald-accent/10 border border-emerald-accent/20 flex-shrink-0">
                  <i className={`${rec.icon} text-emerald-accent text-sm`} />
                </div>
                <div>
                  <h5 className="text-sm font-bold text-white">{rec.product}</h5>
                  <p className="text-xs text-silver-300 mt-0.5 leading-relaxed">{rec.tagline}</p>
                </div>
              </div>
              <ul className="space-y-1.5 pl-1">
                {rec.bullets.slice(0, 3).map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-silver-300">
                    <i className="ri-check-line text-emerald-accent text-[10px] mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {rec.slug && (
                <button
                  onClick={() => { onNavigate(`/products/${rec.slug}`); onClose(); }}
                  className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-accent hover:text-white transition-colors cursor-pointer"
                >
                  Learn more
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs" />
                  </span>
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Email / Copy */}
      <div className="flex items-center gap-2 mb-5">
        <a
          href={mailtoLink}
          className="inline-flex items-center justify-center gap-1.5 flex-1 px-3 py-2.5 bg-emerald-accent text-white text-xs font-semibold rounded-lg hover:bg-emerald-hover transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-mail-send-line" />
          Email My Results
        </a>
        <button
          onClick={handleCopy}
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-graphite-800/80 border border-graphite-700/60 text-silver-300 text-xs font-medium rounded-lg hover:bg-graphite-800 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
        >
          {copied ? (
            <>
              <i className="ri-check-double-line text-emerald-accent" />
              Copied
            </>
          ) : (
            <>
              <i className="ri-file-copy-line" />
              Copy
            </>
          )}
        </button>
      </div>

      {/* CTAs */}
      <div className="space-y-2.5">
        <button onClick={onBookDemo} className="btn-primary w-full justify-center">
          <i className="ri-calendar-check-line" />
          Book a Demo
        </button>
        <button
          onClick={onRestart}
          className="w-full text-center text-xs text-silver-500 hover:text-silver-300 transition-colors cursor-pointer py-2"
        >
          Restart Advisor
        </button>
      </div>
    </div>
  );
}
